import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'

import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { GENERIC_ERROR_KEY } from '@hapana/shared/constants/errors.constants'
import { useErrorHandler } from '@hapana/shared/web/hooks/errors.hooks'
import { useFeatureTrackerOptional } from '@hapana/shared/web/hooks'
import type { Self } from '@hapana/shared/web/services/gqty/schema.generated'
import {
  getMe,
  updateProfileImage,
  deleteProfileImage,
  updateEmail,
  updateUserName,
  getMeProfileImage,
  invalidateProfileImageCache,
} from '_services/me.service'
import { setCoreUserData } from '_services/core-users.service'
import useAuthStore from '_stores/auth.store'

import { ROUTES } from '../app.constants'
import { ME_CACHE_KEY, ME_CACHE_TTL_MS } from '_constants/me-cache.constants'

interface MeQueryData {
  me: Self
}

// Save me data to localStorage for stale-while-revalidate pattern
const saveMeToStorage = (data: MeQueryData, userId: string) => {
  try {
    const cacheEntry = {
      data,
      userId,
      cachedAt: Date.now(),
    }
    localStorage.setItem(ME_CACHE_KEY, JSON.stringify(cacheEntry))
  } catch {
    // Ignore storage errors (quota exceeded, private browsing, etc.)
  }
}

// Get cached me data from localStorage
const getCachedMe = (userId: string): MeQueryData | undefined => {
  try {
    const cached = localStorage.getItem(ME_CACHE_KEY)
    if (!cached) {
      return undefined
    }

    const cacheEntry = JSON.parse(cached) as {
      data: MeQueryData
      userId: string
      cachedAt: number
    }

    // Validate cache belongs to same user
    if (cacheEntry.userId !== userId) {
      localStorage.removeItem(ME_CACHE_KEY)
      return undefined
    }

    // Check TTL
    const age = Date.now() - cacheEntry.cachedAt
    if (age > ME_CACHE_TTL_MS) {
      localStorage.removeItem(ME_CACHE_KEY)
      return undefined
    }

    return cacheEntry.data
  } catch {
    return undefined
  }
}

// Clear me cache (called on logout)
export const clearMeCache = () => {
  try {
    localStorage.removeItem(ME_CACHE_KEY)
  } catch {
    // Ignore storage errors
  }
}

export const useMe = () => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  const location = useLocation()
  const { user, idToken, loading, headersReady } = useAuthStore()

  const isEnabled =
    location.pathname !== ROUTES.login.path &&
    location.pathname !== ROUTES.logout.path &&
    !!user &&
    !!idToken &&
    headersReady &&
    !loading

  const userId = user?.uid

  const result = useQuery<MeQueryData>([getMe.key], getMe.query, {
    onError: (error: unknown) => {
      onError(error as Parameters<typeof onError>[0])
    },
    enabled: isEnabled,
    // Stale-while-revalidate: Show cached data instantly, refresh in background
    initialData: () => {
      if (!userId) {
        return undefined
      }
      return getCachedMe(userId)
    },
    // Mark cached data as stale immediately to trigger background refresh
    initialDataUpdatedAt: () => 0,
    // Keep previous data while refetching
    keepPreviousData: true,
    // Cache successful responses
    onSuccess: (data: MeQueryData) => {
      if (userId && data) {
        saveMeToStorage(data, userId)
      }
    },
  })

  return result
}

/**
 * @deprecated This function is kept for backwards compatibility only and is now a no-op.
 * The `useMe` hook no longer requires manual tracking reset; callers can safely stop using this.
 */
export const resetUseMeTracking = () => {}

interface UpdateProfileImageResponse {
  tempUrl: string
  requestedBy: string
  userType: string
}

export const useUpdateProfileImage = (options?: {
  onSuccess?: (data: UpdateProfileImageResponse) => void
  onError?: (error: Error) => void
}) => {
  return useMutation<UpdateProfileImageResponse, Error, { format: string }>(updateProfileImage, {
    ...options,
  })
}

export const useDeleteProfileImage = (options?: {
  onSuccess?: (data: boolean) => void
  onError?: (error: Error) => void
}) => {
  return useMutation<boolean, Error, void>(deleteProfileImage, { ...options })
}

interface UpdateMeResponse {
  updateMe: Partial<Self>
}

export const useUpdateUserName = (options?: {
  onSuccess?: (data: UpdateMeResponse) => void
  onError?: (error: Error) => void
}) => {
  const { popDialog } = useDialog()
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('staff.update')

  const onSuccess = (newData: UpdateMeResponse) => {
    // Capture old values before cache update
    const oldMeData = queryClient.getQueryData<MeQueryData>(getMe.key)
    const oldFirstName = oldMeData?.me?.firstName
    const oldLastName = oldMeData?.me?.lastName

    queryClient.setQueryData<MeQueryData | undefined>(getMe.key, (oldData) => {
      if (!oldData) {
        return oldData
      }
      return {
        me: {
          ...oldData.me,
          ...newData.updateMe,
        } as Self,
      }
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'profile.name',
        settingDisplayName: 'Profile Name',
        getOldValue: () => ({ firstName: oldFirstName, lastName: oldLastName }),
        getNewValue: () => ({
          firstName: newData.updateMe.firstName,
          lastName: newData.updateMe.lastName,
        }),
        getSummary: () =>
          `User name updated from "${oldFirstName} ${oldLastName}" to "${newData.updateMe.firstName} ${newData.updateMe.lastName}"`,
      })
      auditOnSuccess(newData)
    }

    openToast({
      title: t('userProfile.successMessages.username'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })
    popDialog()
    options?.onSuccess?.(newData)
  }

  const onError = (error: Error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'profile.name',
        settingDisplayName: 'Profile Name',
        getSummary: () => 'User name update failed',
      })
      auditOnError(error)
    }

    if (options?.onError) {
      options.onError(error)
    } else {
      openToast({
        title: t('userProfile.errorMessages.username'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    }
  }

  return useMutation<UpdateMeResponse, Error, { firstName: string; lastName: string }>(
    updateUserName,
    { onSuccess, onError, ...options }
  )
}

export const useUpdateEmail = (options?: {
  onSuccess?: (data: UpdateMeResponse) => void
  onError?: (error: Error) => void
}) => {
  const { popDialog } = useDialog()
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('staff.update')

  const onSuccess = (newData: UpdateMeResponse) => {
    // Capture old value before cache update
    const oldMeData = queryClient.getQueryData<MeQueryData>(getMe.key)
    const oldEmail = oldMeData?.me?.email

    queryClient.setQueryData<MeQueryData | undefined>(getMe.key, (oldData) => {
      if (!oldData) {
        return oldData
      }
      return {
        me: {
          ...oldData.me,
          ...newData.updateMe,
        } as Self,
      }
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'profile.email',
        settingDisplayName: 'Profile Email',
        getOldValue: () => oldEmail,
        getNewValue: () => newData.updateMe.email,
        getSummary: () => `User email updated from "${oldEmail}" to "${newData.updateMe.email}"`,
      })
      auditOnSuccess(newData)
    }

    openToast({
      title: t('userProfile.successMessages.email'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })
    popDialog()
    options?.onSuccess?.(newData)
  }

  const onError = (error: Error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'profile.email',
        settingDisplayName: 'Profile Email',
        getSummary: () => 'User email update failed',
      })
      auditOnError(error)
    }

    const isDuplicate = error?.message?.includes('duplicate key error')
    if (options?.onError) {
      options.onError(error)
    } else if (isDuplicate) {
      openToast({
        title: t('userProfile.errorMessages.emailDuplicate'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    } else {
      openToast({
        title: t('userProfile.errorMessages.email'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    }
  }

  return useMutation<UpdateMeResponse, Error, { email: string }>(updateEmail, {
    onSuccess,
    onError,
    ...options,
  })
}

export const useSetCoreUserData = () => {
  const queryClient = useQueryClient()

  const onSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: getMe.key })
  }

  const onError = (_error: unknown) => {}
  return useMutation(setCoreUserData, { onSuccess, onError })
}

/**
 * Hook to lazy-load the user's profile image.
 * This is separate from useMe to avoid blocking the initial page load.
 * The signed URL generation takes ~1400ms, so we load it after the main data.
 *
 * @param enabled - Set to true to auto-fetch (default: false for manual control)
 */
export const useMeProfileImage = ({
  type = 'thumbnail',
  format = 'webp',
  enabled = false,
} = {}) => {
  return useQuery({
    queryKey: [getMeProfileImage.key, { type, format }],
    queryFn: ({ queryKey }) => getMeProfileImage.query({ queryKey }),
    enabled,
    retry: false,
    // Cache profile image URLs for 25 minutes (signed URLs expire in 30 min)
    staleTime: 25 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  })
}

export const useInvalidateProfileImageCache = () => {
  const queryClient = useQueryClient()

  return useMutation(invalidateProfileImageCache, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getMeProfileImage.key] })
    },
  })
}
